import React from 'react';
import PropTypes from 'prop-types';
import { 
    Box, 
    Text, 
    Button, 
    VStack, 
    Heading, 
    useColorModeValue,
    Icon,
    Container,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaHome, FaRedo } from 'react-icons/fa';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            error: error,
            errorInfo: errorInfo,
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return <ErrorFallback 
                error={this.state.error}
                onReload={this.handleReload}
                onGoHome={this.handleGoHome}
            />;
        }

        return this.props.children;
    }
}

// PropTypes para ErrorBoundary
ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
};

// Componente de fallback mejorado
const ErrorFallback = ({ onReload, onGoHome }) => {
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');
    const textColor = useColorModeValue('gray.800', 'white');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    return (
        <Box
            minH="100vh"
            bg={bgColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={4}
        >
            <Container maxW="md">
                <Box
                    bg={cardBg}
                    borderRadius="xl"
                    boxShadow="xl"
                    p={8}
                    textAlign="center"
                    border="1px solid"
                    borderColor={borderColor}
                >
                    {/* Icono de error */}
                    <VStack spacing={6}>
                        <Box
                            w="80px"
                            h="80px"
                            borderRadius="full"
                            bg="red.100"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mx="auto"
                            mb={4}
                        >
                            <Icon 
                                as={FaExclamationTriangle} 
                                w="40px" 
                                h="40px" 
                                color="red.500" 
                            />
                        </Box>

                        <VStack spacing={4}>
                            <Heading 
                                size="xl" 
                                color="red.500"
                                fontWeight="bold"
                            >
                                ¡Ups! Algo salió mal
                            </Heading>
                            
                            <Text 
                                fontSize="lg" 
                                color={textColor}
                                opacity={0.8}
                                lineHeight="tall"
                            >
                                Ha ocurrido un error inesperado. 
                                No te preocupes, nuestro equipo técnico ya ha sido notificado.
                            </Text>
                        </VStack>

                        {/* Botones de acción */}
                        <VStack spacing={3} w="full" pt={4}>
                            <Button
                                leftIcon={<FaRedo />}
                                colorScheme="blue"
                                onClick={onReload}
                                size="lg"
                                w="full"
                                borderRadius="lg"
                                _hover={{
                                    transform: 'translateY(-2px)',
                                    boxShadow: 'lg',
                                }}
                                transition="all 0.2s"
                            >
                                Recargar página
                            </Button>
                            
                            <Button
                                leftIcon={<FaHome />}
                                variant="outline"
                                onClick={onGoHome}
                                size="lg"
                                w="full"
                                borderRadius="lg"
                                _hover={{
                                    transform: 'translateY(-2px)',
                                    boxShadow: 'md',
                                }}
                                transition="all 0.2s"
                            >
                                Ir al inicio
                            </Button>
                        </VStack>

                        {/* Información adicional */}
                        <Text 
                            fontSize="sm" 
                            color="gray.500"
                            mt={6}
                            opacity={0.7}
                        >
                            Si el problema persiste, no dudes en contactarnos
                        </Text>

                        {/* Botón de prueba solo en desarrollo */}
                        {process.env.NODE_ENV === 'development' && (
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => {
                                    throw new Error('Error de prueba para desarrollo');
                                }}
                                mt={4}
                            >
                                Probar Error (Dev)
                            </Button>
                        )}
                    </VStack>
                </Box>
            </Container>
        </Box>
    );
};

// PropTypes para ErrorFallback
ErrorFallback.propTypes = {
    onReload: PropTypes.func.isRequired,
    onGoHome: PropTypes.func.isRequired,
};

export default ErrorBoundary; 